/**
 * HierarchyCard - Diary-styled cards for hierarchy items
 * Adds washi tape decorations and scaled fonts based on hierarchy level
 */

import React from 'react'
import { useTheme } from '@/components/providers/ThemeProvider'
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/Card'
import { WashiTape } from './DiaryDecorations'

type HierarchyLevel = 'dream' | 'goal' | 'task' | 'skill' | 'habit' | 'action-item' | 'aspiration'

interface HierarchyCardProps {
  level: HierarchyLevel
  title: string
  description?: string
  children?: React.ReactNode
  footer?: React.ReactNode
  className?: string
  showDecoration?: boolean
  onClick?: () => void
}

// Hierarchy level configurations
const hierarchyConfig: Record<HierarchyLevel, {
  washiColor: 'pink' | 'blue' | 'yellow' | 'green' | 'purple'
  accentColor: string
  titleSize: string
  descSize: string
  contentSize: string
  icon: string
  label: string
}> = {
  dream: {
    washiColor: 'purple',
    accentColor: 'var(--horton-purple)',
    titleSize: '1.5rem',    // Largest
    descSize: '1rem',
    contentSize: '1rem',
    icon: '✨',
    label: 'Dream',
  },
  skill: {
    washiColor: 'blue',
    accentColor: 'var(--semantic-success)',
    titleSize: '1.25rem',
    descSize: '0.9rem',
    contentSize: '0.9rem',
    icon: '🔧',
    label: 'Skill',
  },
  habit: {
    washiColor: 'blue',
    accentColor: 'var(--horton-blue)',
    titleSize: '1.25rem',
    descSize: '0.9rem',
    contentSize: '0.9rem',
    icon: '🔄',
    label: 'Habit',
  },
  aspiration: {
    washiColor: 'blue',
    accentColor: 'var(--horton-blue)',
    titleSize: '1.35rem',
    descSize: '0.95rem',
    contentSize: '0.95rem',
    icon: '🌟',
    label: 'Aspiration',
  },
  goal: {
    washiColor: 'green',
    accentColor: 'var(--semantic-success)',
    titleSize: '1.25rem',
    descSize: '0.9rem',
    contentSize: '0.9rem',
    icon: '🎯',
    label: 'Goal',
  },
  task: {
    washiColor: 'yellow',
    accentColor: 'var(--accent-gold)',
    titleSize: '1.1rem',
    descSize: '0.875rem',
    contentSize: '0.875rem',
    icon: '📋',
    label: 'Task',
  },
  'action-item': {
    washiColor: 'pink',
    accentColor: 'var(--bookmark-red)',
    titleSize: '1rem',       // Smallest
    descSize: '0.85rem',
    contentSize: '0.85rem',
    icon: '✓',
    label: 'Action',
  },
}

export const HierarchyCard: React.FC<HierarchyCardProps> = ({
  level,
  title,
  description,
  children,
  footer,
  className = '',
  showDecoration = true,
  onClick,
}) => {
  const { isDiary } = useTheme()
  const config = hierarchyConfig[level]

  if (!isDiary) {
    // Non-diary mode - render simple card
    return (
      <Card className={className} onClick={onClick} style={{ cursor: onClick ? 'pointer' : undefined }}>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          {description && <CardDescription>{description}</CardDescription>}
        </CardHeader>
        {children && <CardContent>{children}</CardContent>}
        {footer && <CardFooter>{footer}</CardFooter>}
      </Card>
    )
  }

  // Diary mode - add decorations and scaled fonts
  return (
    <Card 
      variant="journal" 
      className={`relative overflow-visible ${className}`}
      onClick={onClick}
      style={{ 
        cursor: onClick ? 'pointer' : undefined,
        borderLeftColor: config.accentColor,
      }}
    >
      {/* Washi tape decoration */}
      {showDecoration && (
        <WashiTape 
          color={config.washiColor} 
          width={70 + (5 - Object.keys(hierarchyConfig).indexOf(level)) * 10} 
          rotation={-4 + Math.random() * 4}
          position={{ top: '-12px', left: `${10 + Math.random() * 20}px` }}
          pattern="stripes"
        />
      )}

      {/* Level indicator badge */}
      <div
        className="absolute -top-2 -right-2 px-2 py-0.5 rounded-full text-xs font-medium"
        style={{
          backgroundColor: config.accentColor,
          color: 'var(--paper-cream)',
          fontFamily: "var(--font-ui, 'Nunito', sans-serif)",
          fontSize: '0.7rem',
          boxShadow: '1px 1px 3px rgba(0,0,0,0.15)',
        }}
      >
        {config.icon} {config.label}
      </div>

      <CardHeader className="pt-6">
        <CardTitle 
          style={{ 
            fontSize: config.titleSize,
            fontFamily: "var(--font-handwritten, 'Kalam', cursive)",
            color: config.accentColor,
          }}
        >
          {title}
        </CardTitle>
        {description && (
          <CardDescription 
            style={{ 
              fontSize: config.descSize,
              fontFamily: "var(--font-body, 'Lora', serif)",
            }}
          >
            {description}
          </CardDescription>
        )}
      </CardHeader>

      {children && (
        <CardContent style={{ fontSize: config.contentSize }}>
          {children}
        </CardContent>
      )}

      {footer && (
        <CardFooter style={{ fontSize: config.contentSize }}>
          {footer}
        </CardFooter>
      )}
    </Card>
  )
}

// Compact version for list views
interface HierarchyListItemProps {
  level: HierarchyLevel
  title: string
  subtitle?: string
  status?: 'pending' | 'in-progress' | 'completed'
  onClick?: () => void
  className?: string
}

export const HierarchyListItem: React.FC<HierarchyListItemProps> = ({
  level,
  title,
  subtitle,
  status,
  onClick,
  className = '',
}) => {
  const { isDiary } = useTheme()
  const config = hierarchyConfig[level]

  if (!isDiary) {
    return (
      <div 
        className={`p-3 border rounded-lg hover:bg-surface-alt cursor-pointer ${className}`}
        onClick={onClick}
      >
        <div className="font-medium">{title}</div>
        {subtitle && <div className="text-sm text-muted">{subtitle}</div>}
      </div>
    )
  }

  return (
    <div 
      className={`relative p-3 rounded-lg cursor-pointer diary-hover-lift ${className}`}
      onClick={onClick}
      style={{
        backgroundColor: 'color-mix(in srgb, var(--paper-cream) 60%, transparent)',
        borderLeft: `3px solid ${config.accentColor}`,
        boxShadow: '2px 2px 6px color-mix(in srgb, var(--ink-sepia) 8%, transparent)',
      }}
    >
      {/* Small washi tape accent */}
      <div
        className="absolute -left-1 top-2 w-2 h-6 rounded-r"
        style={{
          backgroundColor: config.accentColor,
          opacity: 0.6,
        }}
      />
      
      <div className="flex items-center gap-2">
        <span style={{ fontSize: '0.9rem' }}>{config.icon}</span>
        <div className="flex-1">
          <div 
            style={{ 
              fontFamily: "var(--font-handwritten, 'Kalam', cursive)",
              fontSize: config.titleSize,
              color: status === 'completed' ? 'var(--ink-faded)' : 'var(--ink-black)',
              textDecoration: status === 'completed' ? 'line-through' : 'none',
            }}
          >
            {title}
          </div>
          {subtitle && (
            <div 
              style={{ 
                fontFamily: "var(--font-body, 'Lora', serif)",
                fontSize: config.descSize,
                color: 'var(--ink-faded)',
              }}
            >
              {subtitle}
            </div>
          )}
        </div>
        
        {/* Status indicator */}
        {status && (
          <div
            className="w-3 h-3 rounded-full"
            style={{
              backgroundColor: 
                status === 'completed' ? 'var(--semantic-success)' : 
                status === 'in-progress' ? 'var(--semantic-warning)' : 
                'var(--paper-margin)',
            }}
          />
        )}
      </div>
    </div>
  )
}

// Breadcrumb-style hierarchy path
interface HierarchyBreadcrumbProps {
  path: Array<{ level: HierarchyLevel; title: string; onClick?: () => void }>
}

export const HierarchyBreadcrumb: React.FC<HierarchyBreadcrumbProps> = ({ path }) => {
  const { isDiary } = useTheme()

  if (!isDiary) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted">
        {path.map((item, i) => (
          <React.Fragment key={i}>
            {i > 0 && <span>/</span>}
            <span 
              className={item.onClick ? 'cursor-pointer hover:text-secondary' : ''}
              onClick={item.onClick}
            >
              {item.title}
            </span>
          </React.Fragment>
        ))}
      </div>
    )
  }

  return (
    <div className="flex items-center gap-1 flex-wrap">
      {path.map((item, i) => {
        const config = hierarchyConfig[item.level]
        return (
          <React.Fragment key={i}>
            {i > 0 && (
              <span style={{ color: 'var(--ink-faded)', margin: '0 4px' }}>›</span>
            )}
            <span
              className={`px-2 py-0.5 rounded ${item.onClick ? 'cursor-pointer hover:opacity-80' : ''}`}
              style={{
                backgroundColor: `color-mix(in srgb, ${config.accentColor} 8%, transparent)`,
                color: config.accentColor,
                fontFamily: "var(--font-handwritten, 'Kalam', cursive)",
                fontSize: '0.85rem',
                border: `1px solid color-mix(in srgb, ${config.accentColor} 19%, transparent)`,
              }}
              onClick={item.onClick}
            >
              {config.icon} {item.title}
            </span>
          </React.Fragment>
        )
      })}
    </div>
  )
}

export default HierarchyCard
